import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";

// ============================================
// CONSTANTS
// ============================================

// Hierarchical expertise tree: Main Categories → Subcategories
const EXPERTISE_TREE = [
  {
    id: 'immigration', name: 'Immigration & Visa', abbr: 'IM', color: '#818cf8',
    description: 'Immigration pathways, visa processing, work permits, study permits, and compliance',
    children: [
      { id: 'im-work-permits', name: 'Work Permits', abbr: 'WP' },
      { id: 'im-study-permits', name: 'Study Permits', abbr: 'SP' },
      { id: 'im-pr', name: 'Permanent Residency', abbr: 'PR' },
      { id: 'im-citizenship', name: 'Citizenship', abbr: 'CT' },
      { id: 'im-family', name: 'Family Sponsorship', abbr: 'FS' },
      { id: 'im-refugee', name: 'Refugee & Asylum', abbr: 'RA' },
      { id: 'im-business', name: 'Business Immigration', abbr: 'BI' },
      { id: 'im-visitor', name: 'Visitor Visas', abbr: 'VV' },
      { id: 'im-compliance', name: 'Compliance & Appeals', abbr: 'CA' },
    ],
  },
  {
    id: 'finance', name: 'Finance & Banking', abbr: 'FB', color: '#60a5fa',
    description: 'Financial services, banking, investment management, and wealth advisory',
    children: [
      { id: 'fn-personal', name: 'Personal Finance', abbr: 'PF' },
      { id: 'fn-corporate', name: 'Corporate Finance', abbr: 'CF' },
      { id: 'fn-investment', name: 'Investment Banking', abbr: 'IB' },
      { id: 'fn-accounting', name: 'Accounting & Bookkeeping', abbr: 'AC' },
      { id: 'fn-tax', name: 'Tax Advisory', abbr: 'TA' },
      { id: 'fn-audit', name: 'Auditing & Compliance', abbr: 'AU' },
      { id: 'fn-wealth', name: 'Wealth Management', abbr: 'WM' },
      { id: 'fn-fintech', name: 'FinTech & Digital Banking', abbr: 'FT' },
      { id: 'fn-crypto', name: 'Crypto & Blockchain', abbr: 'CB' },
      { id: 'fn-forex', name: 'Forex & Trading', abbr: 'FX' },
    ],
  },
  {
    id: 'real-estate', name: 'Real Estate', abbr: 'RE', color: '#34d399',
    description: 'Property transactions, development, management, and investment',
    children: [
      { id: 're-residential', name: 'Residential Sales', abbr: 'RS' },
      { id: 're-commercial', name: 'Commercial Real Estate', abbr: 'CR' },
      { id: 're-mortgage', name: 'Mortgage & Lending', abbr: 'ML' },
      { id: 're-property-mgmt', name: 'Property Management', abbr: 'PM' },
      { id: 're-appraisal', name: 'Appraisal & Valuation', abbr: 'AV' },
      { id: 're-development', name: 'Real Estate Development', abbr: 'RD' },
      { id: 're-investment', name: 'Real Estate Investment', abbr: 'RI' },
      { id: 're-law', name: 'Real Estate Law', abbr: 'RL' },
      { id: 're-rental', name: 'Rental & Leasing', abbr: 'RN' },
    ],
  },
  {
    id: 'legal', name: 'Legal Services', abbr: 'LS', color: '#fbbf24',
    description: 'Legal representation, advisory, compliance, and dispute resolution',
    children: [
      { id: 'lg-corporate', name: 'Corporate Law', abbr: 'CL' },
      { id: 'lg-family', name: 'Family Law', abbr: 'FL' },
      { id: 'lg-criminal', name: 'Criminal Law', abbr: 'CR' },
      { id: 'lg-ip', name: 'Intellectual Property', abbr: 'IP' },
      { id: 'lg-employment', name: 'Employment Law', abbr: 'EL' },
      { id: 'lg-contract', name: 'Contract Law', abbr: 'CT' },
      { id: 'lg-litigation', name: 'Litigation & Disputes', abbr: 'LD' },
      { id: 'lg-compliance', name: 'Regulatory Compliance', abbr: 'RC' },
      { id: 'lg-estate', name: 'Estate & Wills', abbr: 'EW' },
      { id: 'lg-international', name: 'International Law', abbr: 'IL' },
    ],
  },
  {
    id: 'healthcare', name: 'Healthcare & Medical', abbr: 'HC', color: '#2dd4bf',
    description: 'Medical services, healthcare management, and patient care',
    children: [
      { id: 'hc-general', name: 'General Practice', abbr: 'GP' },
      { id: 'hc-specialist', name: 'Specialist Referrals', abbr: 'SR' },
      { id: 'hc-dental', name: 'Dental Care', abbr: 'DC' },
      { id: 'hc-mental', name: 'Mental Health', abbr: 'MH' },
      { id: 'hc-pharma', name: 'Pharmaceutical', abbr: 'PH' },
      { id: 'hc-insurance', name: 'Health Insurance', abbr: 'HI' },
      { id: 'hc-wellness', name: 'Wellness & Prevention', abbr: 'WP' },
      { id: 'hc-veterinary', name: 'Veterinary', abbr: 'VT' },
      { id: 'hc-medtech', name: 'Medical Technology', abbr: 'MT' },
    ],
  },
  {
    id: 'insurance', name: 'Insurance', abbr: 'IN', color: '#f472b6',
    description: 'Insurance products, risk assessment, claims, and underwriting',
    children: [
      { id: 'in-life', name: 'Life Insurance', abbr: 'LI' },
      { id: 'in-health', name: 'Health Insurance', abbr: 'HI' },
      { id: 'in-auto', name: 'Auto Insurance', abbr: 'AI' },
      { id: 'in-home', name: 'Home Insurance', abbr: 'HO' },
      { id: 'in-commercial', name: 'Commercial Insurance', abbr: 'CI' },
      { id: 'in-liability', name: 'Liability Insurance', abbr: 'LB' },
      { id: 'in-travel', name: 'Travel Insurance', abbr: 'TI' },
      { id: 'in-claims', name: 'Claims Management', abbr: 'CM' },
    ],
  },
  {
    id: 'tech', name: 'Technology & IT', abbr: 'IT', color: '#22d3ee',
    description: 'Software, infrastructure, cybersecurity, and digital transformation',
    children: [
      { id: 'it-software', name: 'Software Development', abbr: 'SD' },
      { id: 'it-cloud', name: 'Cloud & Infrastructure', abbr: 'CL' },
      { id: 'it-cyber', name: 'Cybersecurity', abbr: 'CS' },
      { id: 'it-data', name: 'Data & Analytics', abbr: 'DA' },
      { id: 'it-ai', name: 'AI & Machine Learning', abbr: 'ML' },
      { id: 'it-devops', name: 'DevOps & SRE', abbr: 'DO' },
      { id: 'it-consulting', name: 'IT Consulting', abbr: 'IC' },
      { id: 'it-support', name: 'IT Support & Helpdesk', abbr: 'SH' },
      { id: 'it-networking', name: 'Networking & Telecom', abbr: 'NT' },
    ],
  },
  {
    id: 'education', name: 'Education & Training', abbr: 'ED', color: '#fb7185',
    description: 'Academic institutions, professional training, and e-learning',
    children: [
      { id: 'ed-k12', name: 'K-12 Education', abbr: 'K1' },
      { id: 'ed-higher', name: 'Higher Education', abbr: 'HE' },
      { id: 'ed-vocational', name: 'Vocational Training', abbr: 'VT' },
      { id: 'ed-corporate', name: 'Corporate Training', abbr: 'CT' },
      { id: 'ed-elearning', name: 'E-Learning & EdTech', abbr: 'EL' },
      { id: 'ed-tutoring', name: 'Tutoring & Test Prep', abbr: 'TP' },
      { id: 'ed-language', name: 'Language Schools', abbr: 'LS' },
      { id: 'ed-certification', name: 'Certification Programs', abbr: 'CP' },
    ],
  },
  {
    id: 'marketing', name: 'Marketing & Media', abbr: 'MM', color: '#f97316',
    description: 'Digital marketing, branding, advertising, and content creation',
    children: [
      { id: 'mk-digital', name: 'Digital Marketing', abbr: 'DM' },
      { id: 'mk-seo', name: 'SEO & Content', abbr: 'SC' },
      { id: 'mk-social', name: 'Social Media', abbr: 'SM' },
      { id: 'mk-ppc', name: 'PPC & Paid Ads', abbr: 'PA' },
      { id: 'mk-branding', name: 'Branding & Design', abbr: 'BD' },
      { id: 'mk-pr', name: 'Public Relations', abbr: 'PR' },
      { id: 'mk-video', name: 'Video & Production', abbr: 'VP' },
      { id: 'mk-influencer', name: 'Influencer Marketing', abbr: 'IM' },
      { id: 'mk-analytics', name: 'Marketing Analytics', abbr: 'MA' },
    ],
  },
  {
    id: 'hr', name: 'Human Resources', abbr: 'HR', color: '#4ade80',
    description: 'People management, recruitment, benefits, and organizational development',
    children: [
      { id: 'hr-recruiting', name: 'Recruiting & Talent', abbr: 'RT' },
      { id: 'hr-payroll', name: 'Payroll & Benefits', abbr: 'PB' },
      { id: 'hr-training', name: 'Training & Development', abbr: 'TD' },
      { id: 'hr-compliance', name: 'HR Compliance', abbr: 'HC' },
      { id: 'hr-performance', name: 'Performance Management', abbr: 'PM' },
      { id: 'hr-culture', name: 'Culture & Engagement', abbr: 'CE' },
      { id: 'hr-remote', name: 'Remote Work & Policy', abbr: 'RW' },
    ],
  },
  {
    id: 'ecommerce', name: 'E-Commerce & Retail', abbr: 'EC', color: '#e879f9',
    description: 'Online and offline retail, marketplace management, and fulfillment',
    children: [
      { id: 'ec-marketplace', name: 'Marketplace (Amazon, eBay)', abbr: 'MP' },
      { id: 'ec-shopify', name: 'Shopify & DTC', abbr: 'SH' },
      { id: 'ec-fulfillment', name: 'Fulfillment & Logistics', abbr: 'FL' },
      { id: 'ec-payments', name: 'Payments & Checkout', abbr: 'PC' },
      { id: 'ec-crm', name: 'CRM & Retention', abbr: 'CR' },
      { id: 'ec-analytics', name: 'E-Commerce Analytics', abbr: 'EA' },
      { id: 'ec-dropship', name: 'Dropshipping', abbr: 'DS' },
    ],
  },
  {
    id: 'consulting', name: 'Consulting & Advisory', abbr: 'CA', color: '#fde047',
    description: 'Management consulting, strategy, and professional advisory services',
    children: [
      { id: 'cn-management', name: 'Management Consulting', abbr: 'MC' },
      { id: 'cn-strategy', name: 'Strategy Advisory', abbr: 'SA' },
      { id: 'cn-operations', name: 'Operations Consulting', abbr: 'OC' },
      { id: 'cn-change', name: 'Change Management', abbr: 'CM' },
      { id: 'cn-startup', name: 'Startup Advisory', abbr: 'SU' },
      { id: 'cn-m-and-a', name: 'Mergers & Acquisitions', abbr: 'MA' },
    ],
  },
  {
    id: 'construction', name: 'Construction & Architecture', abbr: 'CA', color: '#94a3b8',
    description: 'Building design, construction management, and engineering services',
    children: [
      { id: 'ct-architecture', name: 'Architecture & Design', abbr: 'AD' },
      { id: 'ct-general', name: 'General Contracting', abbr: 'GC' },
      { id: 'ct-civil', name: 'Civil Engineering', abbr: 'CE' },
      { id: 'ct-interior', name: 'Interior Design', abbr: 'ID' },
      { id: 'ct-project', name: 'Project Management', abbr: 'PM' },
      { id: 'ct-inspection', name: 'Building Inspection', abbr: 'BI' },
      { id: 'ct-renovation', name: 'Renovation & Remodeling', abbr: 'RR' },
    ],
  },
  {
    id: 'hospitality', name: 'Hospitality & Tourism', abbr: 'HT', color: '#f9a8d4',
    description: 'Hotels, restaurants, travel, and event management',
    children: [
      { id: 'ht-hotels', name: 'Hotel Management', abbr: 'HM' },
      { id: 'ht-restaurants', name: 'Restaurant & Food', abbr: 'RF' },
      { id: 'ht-travel', name: 'Travel & Tourism', abbr: 'TT' },
      { id: 'ht-events', name: 'Event Planning', abbr: 'EP' },
      { id: 'ht-spa', name: 'Spa & Wellness', abbr: 'SW' },
      { id: 'ht-catering', name: 'Catering Services', abbr: 'CS' },
    ],
  },
  {
    id: 'logistics', name: 'Logistics & Supply Chain', abbr: 'LC', color: '#a3e635',
    description: 'Transportation, warehousing, procurement, and supply chain optimization',
    children: [
      { id: 'lo-freight', name: 'Freight & Shipping', abbr: 'FS' },
      { id: 'lo-warehouse', name: 'Warehousing', abbr: 'WH' },
      { id: 'lo-procurement', name: 'Procurement', abbr: 'PR' },
      { id: 'lo-lastmile', name: 'Last-Mile Delivery', abbr: 'LM' },
      { id: 'lo-inventory', name: 'Inventory Management', abbr: 'IM' },
      { id: 'lo-customs', name: 'Customs & Import/Export', abbr: 'CI' },
    ],
  },
  {
    id: 'automotive', name: 'Automotive', abbr: 'AU', color: '#fdba74',
    description: 'Vehicle sales, services, fleet management, and EV transition',
    children: [
      { id: 'au-sales', name: 'Vehicle Sales', abbr: 'VS' },
      { id: 'au-service', name: 'Auto Service & Repair', abbr: 'SR' },
      { id: 'au-fleet', name: 'Fleet Management', abbr: 'FM' },
      { id: 'au-ev', name: 'Electric Vehicles', abbr: 'EV' },
      { id: 'au-parts', name: 'Parts & Accessories', abbr: 'PA' },
      { id: 'au-insurance', name: 'Auto Insurance', abbr: 'AI' },
    ],
  },
  {
    id: 'energy', name: 'Energy & Environment', abbr: 'EE', color: '#6ee7b7',
    description: 'Energy production, sustainability, and environmental services',
    children: [
      { id: 'en-solar', name: 'Solar & Renewables', abbr: 'SR' },
      { id: 'en-oil-gas', name: 'Oil & Gas', abbr: 'OG' },
      { id: 'en-sustainability', name: 'Sustainability Consulting', abbr: 'SC' },
      { id: 'en-waste', name: 'Waste Management', abbr: 'WM' },
      { id: 'en-carbon', name: 'Carbon Credits & ESG', abbr: 'CC' },
      { id: 'en-utilities', name: 'Utilities & Grid', abbr: 'UG' },
    ],
  },
  {
    id: 'manufacturing', name: 'Manufacturing', abbr: 'MF', color: '#86efac',
    description: 'Production, quality control, and industrial operations',
    children: [
      { id: 'mf-production', name: 'Production Management', abbr: 'PM' },
      { id: 'mf-quality', name: 'Quality Control', abbr: 'QC' },
      { id: 'mf-lean', name: 'Lean Manufacturing', abbr: 'LM' },
      { id: 'mf-automation', name: 'Industrial Automation', abbr: 'IA' },
      { id: 'mf-supply', name: 'Supply Planning', abbr: 'SP' },
      { id: 'mf-safety', name: 'Safety & Compliance', abbr: 'SC' },
    ],
  },
];

// Flat list for backward compatibility (used in overview)
const EXPERTISE_BRAINS = EXPERTISE_TREE.map(cat => ({
  id: cat.id,
  name: cat.name,
  abbr: cat.abbr,
  color: cat.color,
  description: cat.description,
  childCount: cat.children.length,
}));

// Map THIS adviser's stored category (Expert.userCategories — a label/free-text
// string) to the graph node that represents their discipline, so the graph lights
// up the adviser's OWN active brain (e.g. a finance adviser -> "Finance & Banking")
// instead of always immigration. Returns a node id, or null if nothing matches.
function graphIdForCategory(userCategories) {
  const c = (userCategories || '').toLowerCase();
  if (!c) return null;
  const rules = [
    [/immigration|visa|migrat|asylum|refugee/, 'immigration'],
    [/insurance/, 'insurance'],
    [/real\s*estate|property|mortgage/, 'real-estate'],
    [/legal|lawyer|attorney|litigation|\blaw\b/, 'legal'],
    [/financ|invest|wealth|stock|trading|bank|account|\btax\b|audit|bookkeep/, 'finance'],
    [/human\s*resource|\bhr\b|recruit|career|employ|talent/, 'hr'],
    [/e-?commerce|retail|\bshop|store/, 'ecommerce'],
    [/health|wellness|medical|nutrition|fitness|therap|mental|dietit/, 'healthcare'],
    [/education|academ|tutor|school|training|e-?learning/, 'education'],
    [/tech|software|\bit\b|digital|develop|\bdata\b|cyber|cloud|\bai\b/, 'tech'],
    [/market|media|brand|\bseo\b|advertis|content|social/, 'marketing'],
    [/construction|architect|\bbuild/, 'construction'],
    [/hospitality|tourism|travel|event|hotel/, 'hospitality'],
    [/logistic|supply\s*chain|shipping|freight/, 'logistics'],
    [/automotive|vehicle|\bauto\b/, 'automotive'],
    [/energy|environment|sustainab|solar|\bgreen\b/, 'energy'],
    [/manufactur|factory|industrial|production/, 'manufacturing'],
    [/consult|advisor|strateg|coach|personal\s*development|management|business/, 'consulting'],
  ];
  for (const [re, id] of rules) if (re.test(c)) return id;
  return null;
}

const DATA_CATEGORIES = [
  { id: 'opportunity-maps', name: 'Opportunity Maps', color: '#10b981', icon: 'map', countKey: 'opportunityMapsCount', apiPath: '/api/brain/v2/opportunity-maps', responseKey: 'data' },
  { id: 'teachings', name: 'Teachings', color: '#f59e0b', icon: 'lightbulb', countKey: 'teachingsCount', apiPath: '/api/brain/v2/teachings', responseKey: 'data' },
  { id: 'scenario-flows', name: 'Scenario Flows', color: '#3b82f6', icon: 'flow', countKey: 'flowCount', apiPath: '/scenario-flows', responseKey: 'flows' },
  { id: 'rule-sets', name: 'Rule Sets', color: '#8b5cf6', icon: 'rules', countKey: 'ruleCount', apiPath: '/rule-sets', responseKey: 'ruleSets' },
  { id: 'knowledge-categories', name: 'Knowledge', color: '#14b8a6', icon: 'book', countKey: 'knowledgeCount', apiPath: '/context-manager/categories', responseKey: 'categories' },
  { id: 'training-sessions', name: 'Training Sessions', color: '#ec4899', icon: 'training', countKey: 'trainingSessions', apiPath: '/api/brain/v2/training/sessions', responseKey: 'data' },
  { id: 'expert-strategies', name: 'Expert Strategies', color: '#eab308', icon: 'strategy', countKey: 'expertStrategiesCount', apiPath: '/api/expert-strategies', responseKey: 'strategies' },
  { id: 'brain-contexts', name: 'Brain Contexts', color: '#06b6d4', icon: 'brain', countKey: 'brainContextsCount', apiPath: '/context-manager/categories', responseKey: 'categories' },
];

// ============================================
// HELPERS
// ============================================

const getRadialPositions = (cx, cy, radius, count, startAngle = -Math.PI / 2) => {
  return Array.from({ length: count }, (_, i) => {
    const angle = startAngle + (i / count) * 2 * Math.PI;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ============================================
// ANIMATIONS
// ============================================

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.4), 0 0 60px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 30px rgba(99, 102, 241, 0.6), 0 0 80px rgba(99, 102, 241, 0.2); }
`;

const rotateRing = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const rotateRingReverse = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;

const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const GraphContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: radial-gradient(ellipse at center, #0f1629 0%, #080b14 70%, #050710 100%);

  .react-flow__edge-path {
    filter: drop-shadow(0 0 4px currentColor);
  }

  .react-flow__edge.animated path {
    stroke-dasharray: 8 4;
    animation: edgeDash 1.5s linear infinite;
  }

  @keyframes edgeDash {
    to { stroke-dashoffset: -24; }
  }

  .react-flow__minimap {
    background: rgba(10, 13, 20, 0.9) !important;
    border: 1px solid rgba(99, 102, 241, 0.2) !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }

  .react-flow__controls {
    border: 1px solid rgba(99, 102, 241, 0.2) !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4) !important;
  }

  .react-flow__controls-button {
    background: rgba(15, 22, 41, 0.95) !important;
    border-bottom: 1px solid rgba(99, 102, 241, 0.15) !important;
    fill: #a5b4fc !important;
    color: #a5b4fc !important;
    &:hover { background: rgba(99, 102, 241, 0.15) !important; }
  }

  .react-flow__attribution { display: none !important; }
`;

const BreadcrumbBar = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(10, 13, 20, 0.92);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 14px;
  animation: ${fadeInScale} 0.3s ease;
`;

const BreadcrumbItem = styled.span`
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$active ? '#fff' : '#a5b4fc'};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: color 0.2s;
  &:hover { color: ${props => props.$clickable ? '#fff' : '#a5b4fc'}; }
`;

const BreadcrumbSep = styled.span`
  color: rgba(99, 102, 241, 0.4);
  font-size: 12px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 8px;
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.5);
    color: #fff;
  }
`;

const StatsBar = styled.div`
  position: absolute;
  top: 16px;
  right: ${props => props.$panelOpen ? '440px' : '16px'};
  z-index: 10;
  display: flex;
  gap: 12px;
  padding: 10px 18px;
  background: rgba(10, 13, 20, 0.92);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 14px;
  transition: right 0.3s ease;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  span.value {
    font-weight: 700;
    color: ${props => props.$color || '#a5b4fc'};
    font-size: 14px;
  }
`;

const LegendBar = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 16px;
  background: rgba(10, 13, 20, 0.92);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 14px;
  max-width: 500px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  box-shadow: 0 0 6px ${props => hexToRgba(props.$color || '#6366f1', 0.5)};
`;

// ============================================
// NODE STYLES
// ============================================

const UltimateBrainContainer = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  animation: ${breathe} 4s ease-in-out infinite;
`;

const UBRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid transparent;

  &.ring-1 {
    width: 180px; height: 180px;
    border-color: rgba(99, 102, 241, 0.15);
    animation: ${rotateRing} 20s linear infinite;
    border-top-color: rgba(99, 102, 241, 0.5);
    border-right-color: rgba(139, 92, 246, 0.3);
  }
  &.ring-2 {
    width: 160px; height: 160px;
    border-color: rgba(139, 92, 246, 0.1);
    animation: ${rotateRingReverse} 15s linear infinite;
    border-bottom-color: rgba(139, 92, 246, 0.4);
    border-left-color: rgba(99, 102, 241, 0.2);
  }
  &.ring-3 {
    width: 140px; height: 140px;
    border-color: rgba(165, 180, 252, 0.08);
    animation: ${rotateRing} 10s linear infinite;
    border-top-color: rgba(165, 180, 252, 0.3);
  }
`;

const UBCore = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #1e1b4b, #0f0a2a);
  border: 2px solid rgba(99, 102, 241, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.3), 0 0 60px rgba(99, 102, 241, 0.1), inset 0 0 30px rgba(99, 102, 241, 0.1);
  animation: ${pulseGlow} 3s ease-in-out infinite;
`;

const UBLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #a5b4fc;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
  line-height: 1.3;
`;

const UBCount = styled.div`
  font-size: 9px;
  color: rgba(165, 180, 252, 0.6);
  margin-top: 4px;
`;

const PerfectBrainContainer = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s, filter 0.3s;
  animation: ${fadeInScale} 0.4s ease;
  &:hover { transform: scale(1.08); }
`;

const PBCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.$isActive
    ? `radial-gradient(circle at 30% 30%, ${hexToRgba(props.$color, 0.3)}, ${hexToRgba(props.$color, 0.08)})`
    : 'radial-gradient(circle at 30% 30%, rgba(50, 50, 60, 0.4), rgba(30, 30, 40, 0.2))'};
  border: 2px solid ${props => props.$isActive
    ? hexToRgba(props.$color, 0.7)
    : 'rgba(100, 100, 120, 0.25)'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$isActive
    ? `0 0 20px ${hexToRgba(props.$color, 0.3)}, 0 0 40px ${hexToRgba(props.$color, 0.1)}`
    : '0 0 10px rgba(0, 0, 0, 0.3)'};
  transition: all 0.3s;
  ${PerfectBrainContainer}:hover & {
    border-color: ${props => props.$isActive
      ? hexToRgba(props.$color, 0.9)
      : 'rgba(100, 100, 120, 0.45)'};
    box-shadow: ${props => props.$isActive
      ? `0 0 30px ${hexToRgba(props.$color, 0.5)}, 0 0 60px ${hexToRgba(props.$color, 0.15)}`
      : '0 0 20px rgba(100, 100, 120, 0.15)'};
  }
`;

const PBAbbr = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.$isActive ? props.$color : 'rgba(150, 150, 170, 0.5)'};
  letter-spacing: 1px;
`;

const PBName = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.$isActive ? '#fff' : 'rgba(150, 150, 170, 0.4)'};
  text-align: center;
  margin-top: 6px;
  max-width: 100px;
  line-height: 1.3;
`;

const PBStatus = styled.div`
  font-size: 8px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  ${props => props.$isActive ? css`
    background: ${hexToRgba(props.$color, 0.2)};
    color: ${props.$color};
    border: 1px solid ${hexToRgba(props.$color, 0.3)};
  ` : css`
    background: rgba(60, 60, 70, 0.2);
    color: rgba(150, 150, 170, 0.4);
    border: 1px solid rgba(80, 80, 90, 0.2);
  `}
`;

const DataCategoryContainer = styled.div`
  width: 180px;
  padding: 14px 16px;
  background: rgba(15, 20, 35, 0.95);
  border: 1.5px solid ${props => hexToRgba(props.$color, 0.4)};
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s;
  animation: ${fadeInScale} 0.4s ease;
  box-shadow: 0 0 15px ${props => hexToRgba(props.$color, 0.1)};
  &:hover {
    border-color: ${props => hexToRgba(props.$color, 0.7)};
    box-shadow: 0 0 25px ${props => hexToRgba(props.$color, 0.25)};
    transform: translateY(-2px);
  }
`;

const DCIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${props => hexToRgba(props.$color, 0.15)};
  border: 1px solid ${props => hexToRgba(props.$color, 0.3)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const DCName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DCCount = styled.div`
  font-size: 10px;
  color: ${props => props.$color};
  font-weight: 500;
  margin-top: 2px;
`;

const DataItemContainer = styled.div`
  width: 170px;
  padding: 12px 14px;
  background: rgba(15, 20, 35, 0.9);
  border: 1px solid ${props => hexToRgba(props.$color, 0.3)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  animation: ${fadeInScale} 0.4s ease;
  box-shadow: 0 0 10px ${props => hexToRgba(props.$color, 0.08)};
  &:hover {
    border-color: ${props => hexToRgba(props.$color, 0.6)};
    box-shadow: 0 0 20px ${props => hexToRgba(props.$color, 0.2)};
    transform: translateY(-1px);
  }
`;

const DIName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DISubtext = styled.div`
  font-size: 9px;
  color: ${props => props.$color || 'rgba(165, 180, 252, 0.5)'};
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Detail Panel Styles
const DetailPanelOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 420px;
  height: 100%;
  z-index: 20;
  background: rgba(10, 13, 20, 0.97);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  flex-direction: column;
  transform: translateX(${props => props.$open ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.4);
`;

const DPHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  gap: 14px;
`;

const DPHeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => hexToRgba(props.$color || '#6366f1', 0.15)};
  border: 1px solid ${props => hexToRgba(props.$color || '#6366f1', 0.3)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$color || '#6366f1'};
`;

const DPTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const DPSubtitle = styled.div`
  font-size: 12px;
  color: rgba(165, 180, 252, 0.6);
  margin-top: 2px;
`;

const DPCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: rgba(99, 102, 241, 0.2); color: #fff; }
`;

const DPBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.3) transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.3); border-radius: 4px; }
`;

const DPSection = styled.div`
  margin-bottom: 20px;
`;

const DPSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(165, 180, 252, 0.5);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
`;

const DPStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const DPStatCard = styled.div`
  padding: 12px 14px;
  background: rgba(25, 30, 50, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.12);
  border-radius: 10px;
`;

const DPStatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.$color || '#a5b4fc'};
`;

const DPStatLabel = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
`;

const DPDescription = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  padding: 12px 14px;
  background: rgba(25, 30, 50, 0.4);
  border-radius: 10px;
  border: 1px solid rgba(99, 102, 241, 0.08);
`;

const DPListItem = styled.div`
  padding: 12px 14px;
  background: rgba(25, 30, 50, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 10px;
  margin-bottom: 8px;
`;

const DPListTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

const DPListSubtext = styled.div`
  font-size: 11px;
  color: rgba(165, 180, 252, 0.5);
  margin-top: 3px;
`;

const DPBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  font-size: 9px;
  font-weight: 600;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => hexToRgba(props.$color || '#6366f1', 0.15)};
  color: ${props => props.$color || '#a5b4fc'};
  border: 1px solid ${props => hexToRgba(props.$color || '#6366f1', 0.3)};
  margin-left: 8px;
`;

const DPPathwayCard = styled.div`
  padding: 14px;
  background: rgba(25, 30, 50, 0.5);
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 10px;
  margin-bottom: 10px;
`;

const DPPathwayName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 6px;
`;

const DPPathwayMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const DPTag = styled.span`
  padding: 3px 8px;
  font-size: 9px;
  font-weight: 500;
  border-radius: 6px;
  background: ${props => hexToRgba(props.$color || '#6366f1', 0.12)};
  color: ${props => props.$color || '#a5b4fc'};
  border: 1px solid ${props => hexToRgba(props.$color || '#6366f1', 0.2)};
`;

const ExploreButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  color: #a5b4fc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 16px;
  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.2) 100%);
    border-color: rgba(99, 102, 241, 0.5);
    color: #fff;
  }
`;

// ============================================
// SVG ICONS
// ============================================

const NodeIcons = {
  map: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>,
  lightbulb: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="9" y1="18" x2="15" y2="18" /><line x1="10" y1="22" x2="14" y2="22" /><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" /></svg>,
  flow: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" /></svg>,
  rules: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  book: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>,
  training: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3h6v5l4 9a2 2 0 0 1-1.8 2.9H6.8A2 2 0 0 1 5 17l4-9V3z" /><path d="M9 3h6" /></svg>,
  strategy: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>,
  brain: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" /><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" /></svg>,
  close: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  back: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>,
  brainLg: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" /><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" /></svg>,
};

// ============================================
// CUSTOM NODE COMPONENTS
// ============================================

const handleStyle = { opacity: 0, width: 1, height: 1 };

const UltimateBrainNode = ({ data }) => (
  <UltimateBrainContainer>
    <Handle type="source" position={Position.Top} style={handleStyle} />
    <Handle type="source" position={Position.Bottom} style={handleStyle} />
    <Handle type="source" position={Position.Left} style={handleStyle} />
    <Handle type="source" position={Position.Right} style={handleStyle} />
    <UBRing className="ring-1" />
    <UBRing className="ring-2" />
    <UBRing className="ring-3" />
    <UBCore>
      <span style={{ color: '#a5b4fc', marginBottom: 4 }}>{NodeIcons.brainLg}</span>
      <UBLabel>Ultimate<br />Brain</UBLabel>
      <UBCount>{data.activeCount} Active / {data.totalCount} Total</UBCount>
    </UBCore>
  </UltimateBrainContainer>
);

const PerfectBrainNode = ({ data }) => (
  <PerfectBrainContainer>
    <Handle type="target" position={Position.Top} style={handleStyle} />
    <Handle type="target" position={Position.Bottom} style={handleStyle} />
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <Handle type="target" position={Position.Right} style={handleStyle} />
    <Handle type="source" position={Position.Top} id="s-t" style={handleStyle} />
    <Handle type="source" position={Position.Bottom} id="s-b" style={handleStyle} />
    <Handle type="source" position={Position.Left} id="s-l" style={handleStyle} />
    <Handle type="source" position={Position.Right} id="s-r" style={handleStyle} />
    <PBCircle $isActive={data.isActive} $color={data.color}>
      <PBAbbr $isActive={data.isActive} $color={data.color}>{data.abbr}</PBAbbr>
    </PBCircle>
    <PBName $isActive={data.isActive}>{data.name}</PBName>
    <PBStatus $isActive={data.isActive} $color={data.color}>
      {data.isActive ? 'Active' : 'Coming Soon'}
    </PBStatus>
    {data.childCount > 0 && (
      <PBStatus $isActive={false} $color="rgba(255,255,255,0.35)" style={{ fontSize: '7px', marginTop: '-2px' }}>
        {data.childCount} specializations
      </PBStatus>
    )}
  </PerfectBrainContainer>
);

const DataCategoryNode = ({ data }) => (
  <DataCategoryContainer $color={data.color}>
    <Handle type="target" position={Position.Top} style={handleStyle} />
    <Handle type="target" position={Position.Bottom} style={handleStyle} />
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <Handle type="target" position={Position.Right} style={handleStyle} />
    <Handle type="source" position={Position.Top} id="s-t" style={handleStyle} />
    <Handle type="source" position={Position.Bottom} id="s-b" style={handleStyle} />
    <Handle type="source" position={Position.Left} id="s-l" style={handleStyle} />
    <Handle type="source" position={Position.Right} id="s-r" style={handleStyle} />
    <DCIcon $color={data.color}>{NodeIcons[data.icon] || NodeIcons.brain}</DCIcon>
    <div style={{ flex: 1, minWidth: 0 }}>
      <DCName>{data.name}</DCName>
      <DCCount $color={data.color}>{data.count !== undefined ? `${data.count} items` : 'View details'}</DCCount>
    </div>
  </DataCategoryContainer>
);

const DataItemNode = ({ data }) => (
  <DataItemContainer $color={data.color}>
    <Handle type="target" position={Position.Top} style={handleStyle} />
    <Handle type="target" position={Position.Bottom} style={handleStyle} />
    <Handle type="target" position={Position.Left} style={handleStyle} />
    <Handle type="target" position={Position.Right} style={handleStyle} />
    <DIName>{data.name}</DIName>
    <DISubtext $color={data.color}>{data.subtext}</DISubtext>
  </DataItemContainer>
);

const nodeTypes = {
  ultimateBrain: UltimateBrainNode,
  perfectBrain: PerfectBrainNode,
  dataCategory: DataCategoryNode,
  dataItem: DataItemNode,
};

// ============================================
// DETAIL PANEL
// ============================================

const DetailPanel = ({ node, onClose, onExplore, categoryItems }) => {
  if (!node) return null;

  const renderContent = () => {
    if (node.nodeType === 'ultimateBrain') {
      return (
        <>
          <DPSection>
            <DPSectionTitle>Overview</DPSectionTitle>
            <DPDescription>
              The Ultimate Brain is the central intelligence hub that orchestrates all expert knowledge domains.
              Each Perfect Brain specializes in a specific field, containing tailored opportunity maps, teachings,
              scenario flows, and decision rules.
            </DPDescription>
          </DPSection>
          <DPSection>
            <DPSectionTitle>Statistics</DPSectionTitle>
            <DPStatGrid>
              <DPStatCard><DPStatValue $color="#10b981">{node.activeCategoryId ? 1 : 0}</DPStatValue><DPStatLabel>Active Brains</DPStatLabel></DPStatCard>
              <DPStatCard><DPStatValue $color="#94a3b8">{EXPERTISE_BRAINS.length - (node.activeCategoryId ? 1 : 0)}</DPStatValue><DPStatLabel>Coming Soon</DPStatLabel></DPStatCard>
              <DPStatCard><DPStatValue $color="#818cf8">{EXPERTISE_BRAINS.length}</DPStatValue><DPStatLabel>Total Domains</DPStatLabel></DPStatCard>
              <DPStatCard><DPStatValue $color="#f59e0b">v2.0</DPStatValue><DPStatLabel>Architecture</DPStatLabel></DPStatCard>
            </DPStatGrid>
          </DPSection>
          <DPSection>
            <DPSectionTitle>Active Domains</DPSectionTitle>
            {EXPERTISE_BRAINS.filter(b => b.id === node.activeCategoryId).map(b => (
              <DPListItem key={b.id}>
                <DPListTitle>{b.name} <DPBadge $color={b.color}>Active</DPBadge></DPListTitle>
                <DPListSubtext>{b.description}</DPListSubtext>
              </DPListItem>
            ))}
          </DPSection>
        </>
      );
    }

    if (node.nodeType === 'perfectBrain') {
      const treeCat = EXPERTISE_TREE.find(c => c.id === node.id);
      if (!node.isActive) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: 20, fontWeight: 700, color: node.color }}>{node.abbr}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 8 }}>{node.name}</div>
            <div style={{ fontSize: 13, color: 'rgba(165,180,252,0.6)', lineHeight: 1.6, maxWidth: 300 }}>
              This expertise brain is currently in development.
              Once activated, it will contain specialized knowledge for {node.name.toLowerCase()}.
            </div>
            <DPDescription style={{ marginTop: 20 }}>{node.description}</DPDescription>
            {treeCat && treeCat.children.length > 0 && (
              <DPSection style={{ marginTop: 20, textAlign: 'left', width: '100%' }}>
                <DPSectionTitle>Planned Specializations ({treeCat.children.length})</DPSectionTitle>
                {treeCat.children.map(child => (
                  <DPListItem key={child.id}>
                    <DPListTitle>{child.name} <DPBadge $color="#94a3b8">Planned</DPBadge></DPListTitle>
                  </DPListItem>
                ))}
              </DPSection>
            )}
          </div>
        );
      }

      return (
        <>
          <DPSection>
            <DPSectionTitle>Brain Status</DPSectionTitle>
            <DPDescription>{node.description}</DPDescription>
          </DPSection>
          {node.stats && (
            <DPSection>
              <DPSectionTitle>Intelligence Metrics</DPSectionTitle>
              <DPStatGrid>
                <DPStatCard><DPStatValue $color="#10b981">{node.stats.health?.score || 0}</DPStatValue><DPStatLabel>Health Score</DPStatLabel></DPStatCard>
                <DPStatCard><DPStatValue $color="#818cf8">{node.stats.brain?.completionScore || 0}%</DPStatValue><DPStatLabel>Completion</DPStatLabel></DPStatCard>
                <DPStatCard><DPStatValue $color="#f59e0b">v{node.stats.brain?.currentVersion || 1}</DPStatValue><DPStatLabel>Version</DPStatLabel></DPStatCard>
                <DPStatCard><DPStatValue $color="#ec4899">{node.stats.conversations?.total || 0}</DPStatValue><DPStatLabel>Conversations</DPStatLabel></DPStatCard>
              </DPStatGrid>
            </DPSection>
          )}
          {treeCat && treeCat.children.length > 0 && (
            <DPSection>
              <DPSectionTitle>Specializations ({treeCat.children.length})</DPSectionTitle>
              {treeCat.children.map(child => (
                <DPListItem key={child.id}>
                  <DPListTitle>{child.name} <DPBadge $color={node.color}>Active</DPBadge></DPListTitle>
                </DPListItem>
              ))}
            </DPSection>
          )}
          <ExploreButton onClick={() => onExplore && onExplore(node)}>Explore Brain Connections</ExploreButton>
        </>
      );
    }

    if (node.nodeType === 'subcategory') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 0' }}>
          <DPSection>
            <DPSectionTitle>Specialization</DPSectionTitle>
            <DPDescription>{node.description || `${node.name} under ${node.parentName}`}</DPDescription>
          </DPSection>
          <DPSection>
            <DPSectionTitle>Status</DPSectionTitle>
            <DPStatGrid>
              <DPStatCard>
                <DPStatValue $color={node.isActive ? '#10b981' : '#94a3b8'}>{node.isActive ? 'Active' : 'Planned'}</DPStatValue>
                <DPStatLabel>{node.isActive ? 'Ready to use' : 'Coming Soon'}</DPStatLabel>
              </DPStatCard>
            </DPStatGrid>
          </DPSection>
          {node.isActive && (
            <ExploreButton onClick={() => onExplore && onExplore(node)}>Explore Brain Data</ExploreButton>
          )}
        </div>
      );
    }

    if (node.nodeType === 'dataCategory') {
      return (
        <>
          <DPSection>
            <DPSectionTitle>Category</DPSectionTitle>
            <DPDescription>{node.name} - Contains {node.count || 0} items that define how the brain processes and responds in this domain.</DPDescription>
          </DPSection>
          {categoryItems && categoryItems.length > 0 && (
            <DPSection>
              <DPSectionTitle>Items ({categoryItems.length})</DPSectionTitle>
              {categoryItems.map((item, idx) => (
                <DPListItem key={idx}>
                  <DPListTitle>{item.name || item.domain || item.domainDisplayName || `Item ${idx + 1}`}</DPListTitle>
                  <DPListSubtext>{item.description || item.domainDisplayName || item.brainContext || ''}</DPListSubtext>
                </DPListItem>
              ))}
            </DPSection>
          )}
          <ExploreButton onClick={() => onExplore && onExplore(node)}>
            {node.isExpanded ? 'Collapse in Graph' : 'Expand in Graph'}
          </ExploreButton>
        </>
      );
    }

    if (node.nodeType === 'dataItem') {
      return (
        <>
          <DPSection>
            <DPSectionTitle>Details</DPSectionTitle>
            <DPDescription>{node.description || node.subtext || 'Item details'}</DPDescription>
          </DPSection>
          {node.itemData?.pathways && (
            <DPSection>
              <DPSectionTitle>Pathways ({node.itemData.pathways.length})</DPSectionTitle>
              {node.itemData.pathways.map((pw, idx) => (
                <DPPathwayCard key={idx}>
                  <DPPathwayName>{pw.displayName || pw.name}</DPPathwayName>
                  <DPListSubtext>{pw.description}</DPListSubtext>
                  <DPPathwayMeta>
                    {pw.requirements && <DPTag $color="#3b82f6">{pw.requirements.length} Requirements</DPTag>}
                    {pw.blockers && <DPTag $color="#ef4444">{pw.blockers.length} Blockers</DPTag>}
                    {pw.unlockFactors && <DPTag $color="#10b981">{pw.unlockFactors.length} Unlock Factors</DPTag>}
                    {pw.alternatives && <DPTag $color="#f59e0b">{pw.alternatives.length} Alternatives</DPTag>}
                  </DPPathwayMeta>
                </DPPathwayCard>
              ))}
            </DPSection>
          )}
          {node.itemData?.stats && (
            <DPSection>
              <DPSectionTitle>Performance</DPSectionTitle>
              <DPStatGrid>
                <DPStatCard><DPStatValue $color="#10b981">{node.itemData.stats.timesApplied || 0}</DPStatValue><DPStatLabel>Times Applied</DPStatLabel></DPStatCard>
                <DPStatCard><DPStatValue $color="#f59e0b">{Math.round((node.itemData.stats.successRate || 0) * 100)}%</DPStatValue><DPStatLabel>Success Rate</DPStatLabel></DPStatCard>
              </DPStatGrid>
            </DPSection>
          )}
          {node.itemData?.rules && (
            <DPSection>
              <DPSectionTitle>Rules ({node.itemData.rules.length})</DPSectionTitle>
              {node.itemData.rules.map((rule, idx) => (
                <DPListItem key={idx}>
                  <DPListTitle>{rule.name} <DPBadge $color={rule.isActive ? '#10b981' : '#94a3b8'}>{rule.isActive ? 'Active' : 'Inactive'}</DPBadge></DPListTitle>
                  <DPListSubtext>{rule.description || `${rule.conditions?.length || 0} conditions, ${rule.actions?.length || 0} actions`}</DPListSubtext>
                </DPListItem>
              ))}
            </DPSection>
          )}
          {node.itemData?.scenarios && (
            <DPSection>
              <DPSectionTitle>Scenarios ({node.itemData.scenarios.length})</DPSectionTitle>
              {node.itemData.scenarios.map((sc, idx) => (
                <DPListItem key={idx}>
                  <DPListTitle>{sc.name} <DPBadge $color="#ec4899">{sc.brainContext}</DPBadge></DPListTitle>
                  <DPListSubtext>{sc.description || `${sc.exchanges?.length || 0} exchanges`}</DPListSubtext>
                </DPListItem>
              ))}
            </DPSection>
          )}
          {node.itemData?.nodes && node.itemData?.edges && (
            <DPSection>
              <DPSectionTitle>Flow Structure</DPSectionTitle>
              <DPStatGrid>
                <DPStatCard><DPStatValue $color="#3b82f6">{node.itemData.nodes.length}</DPStatValue><DPStatLabel>Nodes</DPStatLabel></DPStatCard>
                <DPStatCard><DPStatValue $color="#8b5cf6">{node.itemData.edges.length}</DPStatValue><DPStatLabel>Connections</DPStatLabel></DPStatCard>
              </DPStatGrid>
            </DPSection>
          )}
        </>
      );
    }

    return null;
  };

  const nodeColor = node.color || '#6366f1';
  const nodeIcon = node.nodeType === 'ultimateBrain' ? 'UB'
    : node.nodeType === 'perfectBrain' ? node.abbr
    : node.nodeType === 'subcategory' ? (node.name?.[0] || 'S')
    : node.nodeType === 'dataCategory' ? (NodeIcons[node.icon] || node.name?.[0])
    : (node.name?.[0] || '?');

  return (
    <DetailPanelOverlay $open>
      <DPHeader>
        <DPHeaderIcon $color={nodeColor}>{nodeIcon}</DPHeaderIcon>
        <div style={{ flex: 1 }}>
          <DPTitle>{node.name}</DPTitle>
          <DPSubtitle>
            {node.nodeType === 'ultimateBrain' && 'Central Intelligence Hub'}
            {node.nodeType === 'perfectBrain' && (node.isActive ? 'Active Expert Brain' : 'Planned Expert Brain')}
            {node.nodeType === 'subcategory' && `${node.parentName} Specialization`}
            {node.nodeType === 'dataCategory' && 'Knowledge Category'}
            {node.nodeType === 'dataItem' && 'Knowledge Item'}
          </DPSubtitle>
        </div>
        <DPCloseBtn onClick={onClose}>{NodeIcons.close}</DPCloseBtn>
      </DPHeader>
      <DPBody>{renderContent()}</DPBody>
    </DetailPanelOverlay>
  );
};

// ============================================
// INNER COMPONENT (uses useReactFlow)
// ============================================

const BrainGraphContent = () => {
  const axios = useAuthenticatedAxios();
  const reactFlowRef = useRef(null);
  const { fitView } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [viewLevel, setViewLevel] = useState('overview');
  const [selectedBrainId, setSelectedBrainId] = useState(null);
  const [expandedCats, setExpandedCats] = useState([]);
  const [expandedDirectories, setExpandedDirectories] = useState([]);
  const [detailNode, setDetailNode] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [categoryItemsData, setCategoryItemsData] = useState({});
  const categoryItemsRef = useRef({});
  const [categoryItemsForPanel, setCategoryItemsForPanel] = useState(null);
  // The graph node id that represents THIS adviser's discipline (their active brain).
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get('/api/brain/my-brain/dashboard');
        if (response.data) setDashboardData(response.data);
      } catch (err) {
        console.error('Failed to fetch brain dashboard:', err);
      }
    };
    if (axios) fetchDashboard();
  }, [axios]);

  // Resolve which brain is "active" for this adviser from their profile category.
  useEffect(() => {
    if (!axios) return;
    let cancelled = false;
    axios.get('/profile').then(res => {
      if (cancelled) return;
      const cats = res?.data?.profile?.userCategories ?? res?.data?.userCategories ?? '';
      setActiveCategoryId(graphIdForCategory(cats));
    }).catch(err => console.error('Failed to resolve active brain category:', err));
    return () => { cancelled = true; };
  }, [axios]);

  const fetchCategoryItems = useCallback(async (categoryId) => {
    if (categoryItemsRef.current[categoryId]) return categoryItemsRef.current[categoryId];
    const category = DATA_CATEGORIES.find(c => c.id === categoryId);
    if (!category?.apiPath) return [];
    try {
      const response = await axios.get(category.apiPath);
      const rd = response.data;
      const items = (category.responseKey && rd?.[category.responseKey])
        || rd?.data || (Array.isArray(rd) ? rd : []);
      const itemsArray = Array.isArray(items) ? items : [];
      categoryItemsRef.current[categoryId] = itemsArray;
      setCategoryItemsData(prev => ({ ...prev, [categoryId]: itemsArray }));
      return itemsArray;
    } catch (err) {
      console.error(`Failed to fetch ${categoryId}:`, err);
      return [];
    }
  }, [axios]);

  // ---- OVERVIEW GRAPH with expandable directories ----
  const buildOverviewGraph = useCallback((expandedDirs = []) => {
    const newNodes = [];
    const newEdges = [];
    const activeCount = activeCategoryId ? 1 : 0;

    newNodes.push({
      id: 'ultimate-brain',
      type: 'ultimateBrain',
      position: { x: -90, y: -90 },
      data: { activeCount, totalCount: EXPERTISE_BRAINS.length, nodeType: 'ultimateBrain', name: 'Ultimate Brain', activeCategoryId },
    });

    const positions = getRadialPositions(0, 0, 550, EXPERTISE_TREE.length);
    EXPERTISE_TREE.forEach((cat, i) => {
      const catCenterX = positions[i].x;
      const catCenterY = positions[i].y;
      const isExpanded = expandedDirs.includes(cat.id);
      const isActive = cat.id === activeCategoryId;

      newNodes.push({
        id: `brain-${cat.id}`,
        type: 'perfectBrain',
        position: { x: catCenterX - 50, y: catCenterY - 50 },
        data: { ...cat, isActive, nodeType: 'perfectBrain', childCount: cat.children.length },
      });
      newEdges.push({
        id: `ub-${cat.id}`,
        source: 'ultimate-brain',
        target: `brain-${cat.id}`,
        type: 'default',
        animated: isActive,
        style: { stroke: (isActive ? cat.color : 'rgba(80, 80, 100, 0.2)'), strokeWidth: (isActive ? 2.5 : 1) },
      });

      if (isExpanded && cat.children) {
        const outwardAngle = Math.atan2(catCenterY, catCenterX);
        const fanSpread = Math.min(Math.PI * 0.8, Math.max(0.6, cat.children.length * 0.2));
        const childRadius = 200;

        cat.children.forEach((child, j) => {
          const angle = cat.children.length === 1
            ? outwardAngle
            : outwardAngle - fanSpread / 2 + (j / (cat.children.length - 1)) * fanSpread;
          const cx = catCenterX + childRadius * Math.cos(angle) - 85;
          const cy = catCenterY + childRadius * Math.sin(angle) - 25;

          newNodes.push({
            id: `sub-${child.id}`,
            type: 'dataItem',
            position: { x: cx, y: cy },
            data: {
              name: child.name,
              subtext: isActive ? 'Active' : 'Coming Soon',
              color: cat.color,
              nodeType: 'subcategory',
              parentId: cat.id,
              parentName: cat.name,
              isActive,
            },
          });
          newEdges.push({
            id: `${cat.id}-${child.id}`,
            source: `brain-${cat.id}`,
            target: `sub-${child.id}`,
            type: 'default',
            animated: isActive,
            style: { stroke: hexToRgba(cat.color, isActive ? 0.5 : 0.2), strokeWidth: 1.5 },
          });
        });
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
    setTimeout(() => fitView({ padding: 0.2, maxZoom: 1.0, duration: 400 }), 50);
  }, [setNodes, setEdges, fitView, activeCategoryId]);

  // ---- BRAIN GRAPH with in-place category expansion ----
  const buildBrainGraph = useCallback((brainId, expanded = []) => {
    const brain = EXPERTISE_BRAINS.find(b => b.id === brainId);
    if (!brain) return;

    const newNodes = [];
    const newEdges = [];
    const MAX_ITEMS_PER_CAT = 15;

    newNodes.push({
      id: `brain-${brain.id}`,
      type: 'perfectBrain',
      position: { x: -50, y: -50 },
      data: { ...brain, isActive: brain.id === activeCategoryId, expanded: true, nodeType: 'perfectBrain', stats: dashboardData },
    });

    const catPositions = getRadialPositions(0, 0, 350, DATA_CATEGORIES.length);

    DATA_CATEGORIES.forEach((cat, i) => {
      const fetchedItems = categoryItemsRef.current[cat.id];
      const count = fetchedItems ? fetchedItems.length : (dashboardData?.brain?.[cat.countKey] ?? '?');
      const catCenterX = catPositions[i].x;
      const catCenterY = catPositions[i].y;
      const isExpanded = expanded.includes(cat.id);

      newNodes.push({
        id: `cat-${cat.id}`,
        type: 'dataCategory',
        position: { x: catCenterX - 90, y: catCenterY - 30 },
        data: { ...cat, count, nodeType: 'dataCategory', isExpanded },
      });
      newEdges.push({
        id: `${brain.id}-${cat.id}`,
        source: `brain-${brain.id}`,
        target: `cat-${cat.id}`,
        type: 'default',
        animated: true,
        style: { stroke: cat.color, strokeWidth: isExpanded ? 3 : 2 },
      });

      if (isExpanded) {
        const items = categoryItemsRef.current[cat.id] || [];
        const displayItems = items.slice(0, MAX_ITEMS_PER_CAT);
        const hasMore = items.length > MAX_ITEMS_PER_CAT;
        const totalChildNodes = displayItems.length + (hasMore ? 1 : 0);

        if (totalChildNodes === 0) return;

        const outwardAngle = Math.atan2(catCenterY, catCenterX);
        const fanSpread = Math.min(Math.PI * 0.85, Math.max(0.6, totalChildNodes * 0.22));
        const itemRadius = 240;

        displayItems.forEach((item, j) => {
          const angle = totalChildNodes === 1
            ? outwardAngle
            : outwardAngle - fanSpread / 2 + (j / (totalChildNodes - 1)) * fanSpread;
          const ix = catCenterX + itemRadius * Math.cos(angle) - 85;
          const iy = catCenterY + itemRadius * Math.sin(angle) - 25;

          const itemName = item.name || item.domain || item.domainDisplayName || `Item ${j + 1}`;
          const rawSub = item.description || item.domainDisplayName || item.brainContext || item.status || '';
          const itemSubtext = typeof rawSub === 'string' ? (rawSub.length > 45 ? rawSub.substring(0, 45) + '...' : rawSub) : '';

          newNodes.push({
            id: `item-${cat.id}-${j}`,
            type: 'dataItem',
            position: { x: ix, y: iy },
            data: { name: itemName, subtext: itemSubtext, color: cat.color, nodeType: 'dataItem', itemData: item },
          });
          newEdges.push({
            id: `${cat.id}-item-${j}`,
            source: `cat-${cat.id}`,
            target: `item-${cat.id}-${j}`,
            type: 'default',
            animated: true,
            style: { stroke: hexToRgba(cat.color, 0.45), strokeWidth: 1.5 },
          });
        });

        if (hasMore) {
          const angle = totalChildNodes === 1
            ? outwardAngle
            : outwardAngle - fanSpread / 2 + ((totalChildNodes - 1) / (totalChildNodes - 1)) * fanSpread;
          const mx = catCenterX + itemRadius * Math.cos(angle) - 85;
          const my = catCenterY + itemRadius * Math.sin(angle) - 25;

          newNodes.push({
            id: `more-${cat.id}`,
            type: 'dataItem',
            position: { x: mx, y: my },
            data: { name: `+${items.length - MAX_ITEMS_PER_CAT} more...`, subtext: 'Click to see in panel', color: cat.color, nodeType: 'moreItems', categoryId: cat.id },
          });
          newEdges.push({
            id: `${cat.id}-more`,
            source: `cat-${cat.id}`,
            target: `more-${cat.id}`,
            type: 'default',
            style: { stroke: hexToRgba(cat.color, 0.25), strokeWidth: 1, strokeDasharray: '4 4' },
          });
        }
      }
    });

    setNodes(newNodes);
    setEdges(newEdges);
    setTimeout(() => fitView({ padding: 0.2, maxZoom: 1.0, duration: 500 }), 80);
  }, [setNodes, setEdges, dashboardData, fitView, activeCategoryId]);

  useEffect(() => {
    buildOverviewGraph([]);
  }, [buildOverviewGraph]);

  // ---- Toggle expand/collapse a directory in the overview ----
  const toggleDirectoryExpand = useCallback((catId) => {
    setExpandedDirectories(prev => {
      const isExpanded = prev.includes(catId);
      const next = isExpanded ? prev.filter(id => id !== catId) : [...prev, catId];
      buildOverviewGraph(next);
      return next;
    });
  }, [buildOverviewGraph]);

  // ---- Toggle expand/collapse a category in-place (brain view) ----
  const toggleCategoryExpand = useCallback(async (categoryId, brainId) => {
    const isCurrentlyExpanded = expandedCats.includes(categoryId);
    let newExpanded;

    if (isCurrentlyExpanded) {
      newExpanded = expandedCats.filter(id => id !== categoryId);
    } else {
      await fetchCategoryItems(categoryId);
      newExpanded = [...expandedCats, categoryId];
    }

    setExpandedCats(newExpanded);
    buildBrainGraph(brainId, newExpanded);
  }, [expandedCats, fetchCategoryItems, buildBrainGraph]);

  // ---- Node click handler ----
  const handleNodeClick = useCallback(async (event, node) => {
    const data = node.data;

    if (node.type === 'ultimateBrain') {
      setDetailNode({ ...data });
      return;
    }

    if (node.type === 'perfectBrain') {
      if (viewLevel === 'overview') {
        if (data.isActive) {
          setDetailNode({ ...data, stats: dashboardData });
        }
        toggleDirectoryExpand(data.id);
      } else {
        setDetailNode({ ...data, stats: dashboardData });
      }
      return;
    }

    if (data.nodeType === 'subcategory') {
      if (data.isActive) {
        setSelectedBrainId(data.parentId);
        setViewLevel('brain');
        setExpandedCats([]);
        setExpandedDirectories([]);
        buildBrainGraph(data.parentId, []);
      }
      setDetailNode({
        ...data,
        name: data.name,
        description: data.isActive
          ? `Active expertise under ${data.parentName}. Click "Explore Brain" to see its knowledge data.`
          : `This expertise is coming soon. It will be available as part of the ${data.parentName} category.`,
      });
      return;
    }

    if (node.type === 'dataCategory') {
      toggleCategoryExpand(data.id, selectedBrainId);
      const items = categoryItemsRef.current[data.id] || await fetchCategoryItems(data.id);
      setCategoryItemsForPanel(items);
      setDetailNode({ ...data, count: items.length });
      return;
    }

    if (node.type === 'dataItem') {
      if (data.nodeType === 'moreItems' && data.categoryId) {
        const items = categoryItemsRef.current[data.categoryId] || await fetchCategoryItems(data.categoryId);
        setCategoryItemsForPanel(items);
        const cat = DATA_CATEGORIES.find(c => c.id === data.categoryId);
        setDetailNode({ ...cat, count: items.length, nodeType: 'dataCategory', name: cat?.name || data.categoryId });
        return;
      }
      setDetailNode({ ...data });
    }
  }, [viewLevel, dashboardData, buildBrainGraph, fetchCategoryItems, toggleCategoryExpand, toggleDirectoryExpand, selectedBrainId]);

  // ---- Explore from detail panel ----
  const handleExplore = useCallback(async (nodeData) => {
    if (nodeData.nodeType === 'perfectBrain' && nodeData.isActive) {
      setSelectedBrainId(nodeData.id);
      setViewLevel('brain');
      setExpandedCats([]);
      setExpandedDirectories([]);
      buildBrainGraph(nodeData.id, []);
      setDetailNode(null);
    } else if (nodeData.nodeType === 'subcategory' && nodeData.isActive) {
      setSelectedBrainId(nodeData.parentId);
      setViewLevel('brain');
      setExpandedCats([]);
      setExpandedDirectories([]);
      buildBrainGraph(nodeData.parentId, []);
      setDetailNode(null);
    } else if (nodeData.nodeType === 'dataCategory') {
      toggleCategoryExpand(nodeData.id, selectedBrainId);
      setDetailNode(null);
    }
  }, [selectedBrainId, buildBrainGraph, toggleCategoryExpand]);

  // ---- Back navigation ----
  const handleBack = useCallback(() => {
    setDetailNode(null);
    setCategoryItemsForPanel(null);
    if (viewLevel === 'brain') {
      setViewLevel('overview');
      setSelectedBrainId(null);
      setExpandedCats([]);
      buildOverviewGraph(expandedDirectories);
    }
  }, [viewLevel, buildOverviewGraph, expandedDirectories]);

  const selectedBrain = EXPERTISE_BRAINS.find(b => b.id === selectedBrainId);

  return (
    <GraphContainer>
      <BreadcrumbBar>
        {viewLevel !== 'overview' && (
          <BackButton onClick={handleBack}>{NodeIcons.back} Back</BackButton>
        )}
        <BreadcrumbItem
          $clickable={viewLevel !== 'overview'}
          $active={viewLevel === 'overview'}
          onClick={() => viewLevel !== 'overview' && handleBack()}
        >
          Ultimate Brain
        </BreadcrumbItem>
        {selectedBrain && (
          <>
            <BreadcrumbSep>/</BreadcrumbSep>
            <BreadcrumbItem $active>
              {selectedBrain.name}
            </BreadcrumbItem>
          </>
        )}
        {viewLevel === 'overview' && expandedDirectories.length > 0 && (
          <>
            <BreadcrumbSep>/</BreadcrumbSep>
            <BreadcrumbItem $active>
              {expandedDirectories.length} {expandedDirectories.length === 1 ? 'directory' : 'directories'} expanded
            </BreadcrumbItem>
          </>
        )}
        {viewLevel === 'brain' && expandedCats.length > 0 && (
          <>
            <BreadcrumbSep>/</BreadcrumbSep>
            <BreadcrumbItem $active>
              {expandedCats.length} expanded
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbBar>

      {dashboardData && viewLevel === 'overview' && (
        <StatsBar $panelOpen={!!detailNode}>
          <StatItem $color="#10b981"><span className="value">{dashboardData.health?.score || 0}</span> Health</StatItem>
          <StatItem $color="#818cf8"><span className="value">{dashboardData.brain?.completionScore || 0}%</span> Complete</StatItem>
          <StatItem $color="#f59e0b"><span className="value">{dashboardData.conversations?.total || 0}</span> Conversations</StatItem>
        </StatsBar>
      )}

      <ReactFlow
        ref={reactFlowRef}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1.2 }}
        minZoom={0.05}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: 'default', style: { strokeWidth: 2 } }}
      >
        <Background variant={BackgroundVariant.Dots} gap={30} size={1} color="rgba(99, 102, 241, 0.08)" />
        <Controls showInteractive={false} position="bottom-right" style={{ marginBottom: 20, marginRight: 20 }} />
        <MiniMap
          position="bottom-right"
          style={{ marginBottom: 20, marginRight: 80 }}
          maskColor="rgba(10, 13, 20, 0.8)"
          nodeColor={(n) => {
            if (n.type === 'ultimateBrain') return '#6366f1';
            if (n.type === 'perfectBrain') return n.data?.isActive ? (n.data?.color || '#6366f1') : '#333';
            if (n.type === 'dataCategory') return n.data?.color || '#6366f1';
            return n.data?.color || '#6366f1';
          }}
          nodeBorderRadius={8}
        />
      </ReactFlow>

      <LegendBar>
        <LegendItem><LegendDot $color="#6366f1" /><span>Ultimate Brain</span></LegendItem>
        <LegendItem><LegendDot $color="#818cf8" /><span>Active Brain</span></LegendItem>
        <LegendItem><LegendDot $color="#555" /><span>Coming Soon</span></LegendItem>
        {viewLevel === 'overview' && <LegendItem><LegendDot $color="#a78bfa" /><span>Click to expand subcategories</span></LegendItem>}
        {viewLevel !== 'overview' && DATA_CATEGORIES.slice(0, 4).map(cat => (
          <LegendItem key={cat.id}><LegendDot $color={cat.color} /><span>{cat.name}</span></LegendItem>
        ))}
      </LegendBar>

      {detailNode && (
        <DetailPanel
          node={detailNode}
          onClose={() => { setDetailNode(null); setCategoryItemsForPanel(null); }}
          onExplore={handleExplore}
          categoryItems={categoryItemsForPanel}
        />
      )}
    </GraphContainer>
  );
};

// ============================================
// MAIN COMPONENT (wraps with ReactFlowProvider)
// ============================================

const UltimateBrainGraphModule = () => (
  <ReactFlowProvider>
    <BrainGraphContent />
  </ReactFlowProvider>
);

export default UltimateBrainGraphModule;
